/**
 * Centralized plain-Russian copy for user-facing strings (Conventions §6).
 *
 * Goal: keep system jargon (`deal`, `hold`, `TTL`, `410`, `429`,
 * `confirmation_url`, ...) out of the UI. Everything exported from this
 * module is rendered to the end user. If you add a value, do not use
 * stilted or technical terms — write the phrase a real human would say.
 *
 * The companion test `__tests__/strings.spec.ts` walks every exported
 * constant recursively and fails the build if a forbidden token slips in.
 */

import { SECURE_DEAL_ESCROW_PILL } from '@/shared/lib/secureDeal';

/** Booking / hold / payment lifecycle. */
export const BOOKING_COPY = {
    /** Shown on /payment-return when the bank says "pending" long enough. */
    paymentPending:
        'Проверяем оплату. Когда деньги придут — отправим уведомление. Можно закрыть эту страницу.',
    /** Shown when the booking countdown ran out before the user paid. */
    paymentExpired: 'Время на оплату вышло. Можно создать бронирование заново.',
    /** Shown when the bank rejected / cancelled the payment. */
    paymentFailed: 'Платёж не прошёл. Попробуйте создать бронь заново.',
    /** Shown when bank returns expired status. */
    paymentExpiredShort: 'Срок оплаты истёк. Создайте новую бронь.',
    /** Shown when we cannot fetch a checkout URL from the bank. */
    paymentLinkMissing:
        'Не удалось получить ссылку на оплату. Обновите ссылку или повторите бронирование.',
    /** Hint shown above the "pay" button on the consultation payment modal. */
    consultationSlotReserved:
        'Мы зарезервировали выбранный слот для вас. Перейдите к оплате, чтобы подтвердить бронь.',
    /** When the user already has too many unpaid bookings. */
    tooManyActive:
        'У вас уже есть активные брони. Завершите оплату текущих личных сессий, чтобы оформить новую.',
    /** Generic fallback when payment setup fails. */
    paymentSetupFailed: 'Не удалось подготовить оплату. Попробуйте ещё раз.',
    /** Suffix appended to calendar event title when waiting for payment. */
    awaitingPaymentSuffix: ' — ожидает оплату',
    /** Suffix appended when the payment window has closed. */
    overduePaymentSuffix: ' — оплата просрочена',
    /** Aria/tooltip label for the pill on the day-summary row. */
    paymentPillLabel: 'Оплата',
} as const;

/** Money / escrow status labels shown on session and deal cards. */
export const MONEY_STATE_COPY = {
    /**
     * Оплата заморожена до окончания сессии. E60: подпись берётся из единого
     * источника объяснения безопасной сделки — «у кого» лежат деньги мы не
     * называем (по оферте резервирование выполняет Банк).
     */
    escrowHeld: SECURE_DEAL_ESCROW_PILL,
    /** Money has been paid out to the mentor after confirmation. */
    released: '✓ Деньги переведены наставнику',
    /** A refund has been initiated but not yet completed. */
    refundPending: '⚠ Возврат в процессе',
    /** Money has been returned to the mentee. */
    refunded: '↩ Деньги возвращены',
} as const;

/** Dispute flow — buttons, banners, toasts. */
export const DISPUTE_COPY = {
    /** Section title on the session page. */
    sectionTitle: 'Спор',
    /** Toast shown when the user just opened a dispute. */
    openedToast: 'Спор открыт. Мы свяжемся с экспертом.',
    /** Status labels for the dispute lifecycle. */
    statusLabels: {
        opened: 'Открыт',
        underReview: 'На рассмотрении',
        resolved: 'Разрешён',
        refunded: 'Возвращены средства',
    },
} as const;

/** Calendar pills / empty states. */
export const CALENDAR_COPY = {
    /** Empty state when the selected date has no events. */
    emptyDayTitle: 'На выбранную дату пока пусто',
    emptyDayHint: 'Выберите другой день или запланируйте событие в другом разделе.',
    /** Empty state for the upcoming events tab. */
    noUpcoming: 'Нет предстоящих событий',
    /** Empty state for the history tab. */
    historyEmpty: 'Ваша история пока пуста',
    /**
     * #69: роли в календаре. «Наставник/Ученик» вместо расплывчатых
     * «Веду/Участвую» — совпадает с глоссарием платформы (mentor/mentee).
     * Единый источник для бейджа карточки (EventCard) и чипов-фильтра (CalendarPage).
     */
    roleLeading: 'Наставник',
    roleParticipating: 'Ученик',
    /** #93: пустая сессия наставника (0 записавшихся) — вместо пустоты. */
    noAttendees: 'На сессию ещё никто не записался',
} as const;

/** Resumable booking card (when the payment window ran out). */
export const RESUMABLE_BOOKING_COPY = {
    sameSlotAvailable: 'Это время ещё свободно — можно завершить оплату.',
    slotTakenAlternatives: 'Это время уже занято — выберите другое.',
    resumePayment: 'Продолжить оплату',
    pickAnother: 'Выбрать другое время',
} as const;

/**
 * Slot conflict / availability errors raised from the booking modal.
 *
 * Important: every string here is shown verbatim to the menti when the
 * reservation step (the "Перейти к оплате" button) returns 409. The wording
 * carries a different meaning to the user — «занят» implies somebody else
 * grabbed the slot, «начался» implies the menti was late. Do not blur these
 * cases into one generic message. See `mapBookingHoldError` in
 * `shared/types/errors.ts` for the canonical mapping.
 */
export const SLOT_COPY = {
    /** Used as a generic fallback only — prefer the specific entries below. */
    slotTaken: 'Этот слот уже занят. Выберите другое время, пожалуйста.',
    reserveFailed: 'Не удалось забронировать слот. Попробуйте ещё раз.',
    /** Slot taken by another menti between the user's pick and the click. */
    slotJustTaken: 'Этот слот только что забронировали — выберите другое.',
    /**
     * 12.11: the slot overlaps something the mentor already committed to
     * (e.g. an event on the same time). Distinct from `slotJustTaken` — nobody
     * else "grabbed" the slot; the time itself is occupied by the mentor's
     * schedule. BE booking-layer guard raises a greppable
     * «occupied by the mentor schedule» detail mapped here.
     */
    slotTimeUnavailable: 'Это время уже занято — выберите другое.',
    /**
     * Slot start time has passed (user clicked «Перейти к оплате» too late).
     * Optionally suffixed with the HH:MM start time, e.g. «Слот уже начался
     * в 18:05 — выберите другое время».
     */
    slotAlreadyStarted: 'Слот уже начался — выберите другое время.',
    slotAlreadyStartedWithTime: (timeHHmm: string) =>
        `Слот уже начался в ${timeHHmm} — выберите другое время.`,
    /** Payment window expired before the menti finished checkout. */
    holdExpired: 'Время на оплату истекло — попробуйте снова.',
    /** Generic 409 fallback when we cannot identify the specific cause. */
    holdGenericFallback: 'Не удалось забронировать слот — попробуйте другой.',
    /**
     * У наставника нет активного способа выплат — платную услугу купить нельзя,
     * пока он не привяжет карту. Отдельная строка нужна потому, что
     * `holdGenericFallback` («попробуйте другой слот») здесь ВРЁТ: любой другой
     * слот той же платной услуги упрётся в тот же гейт. Причину формулируем
     * нейтрально — покупателю не нужны детали чужих настроек, и это не повод
     * обвинять наставника.
     *
     * BE-источники, дающие эту причину (оба ловятся одной подстрокой
     * «способ получения оплаты»):
     *   - `consultations/services/holds.py` → SellerPayoutMissingError (legacy-путь);
     *   - `commerce/application/use_cases/cutover.py` → CommerceCheckoutError (V2).
     */
    sellerPayoutMissing:
        'Сейчас эту услугу нельзя забронировать из-за настроек выплат наставника. ' +
        'Попробуйте выбрать другую услугу или вернитесь позже.',
    /**
     * Menti tried to book a slot that starts sooner than the mentor's
     * minimum-notice window allows. BE returns
     * `{code:"slot_starts_too_soon", min_booking_notice_seconds: N}`; the
     * mapper converts N → hours and feeds it here so the menti sees the
     * exact threshold and stops trying the same slot. See
     * `mapBookingHoldError` in `shared/types/errors.ts`.
     */
    slotStartsTooSoon: (hours: number) => {
        const abs = Math.abs(hours) % 100;
        const last = abs % 10;
        let unit: string;
        if (abs > 10 && abs < 20) {
            unit = 'часов';
        } else if (last === 1) {
            unit = 'час';
        } else if (last > 1 && last < 5) {
            unit = 'часа';
        } else {
            unit = 'часов';
        }
        return `Этот слот начинается слишком скоро. Минимум за ${hours} ${unit} до старта — выберите более поздний.`;
    },
    /**
     * Same condition, but BE did not include `min_booking_notice_seconds` so
     * we cannot quote the threshold. The wording must still tell the menti
     * the slot is too close in time, not "uknown error".
     */
    slotStartsTooSoonGeneric:
        'Этот слот начинается слишком скоро. Выберите более поздний — наставник просит запас по времени.',
} as const;
